/**
 * @file task.form.tsx
 * @description Форма для создания и редактирования задач в системе управления проектами.
 * Включает поля для указания сервиса, проекта, спринта, тегов, приоритета, ответственного,
 * трудозатрат (план/факт), процента выполнения и дат начала/окончания.
 * Автоматически вычисляет плановые даты выполнения на основе зависимостей и рабочей нагрузки.
 * Интегрируется с react-hook-form и автоматически обновляет статусы задач.
 * @author AI Assistant
 * @created 2025-09-03
 * @updated 2025-09-03
 * @dependencies [react, @components/form-controls/date-picker, @components/form-controls/text-field, @components/form-controls/chips-choice, @components/form-controls/dictionary, @consta/uikit/Text, @api/actions/method.service, @consta/uikit/useDebounce, moment, @src/types, axios, @utils/error-helper, @components/form-controls/tag-select]
 * @related-files [@components/form-controls/date-picker/date-picker.component.tsx, @components/form-controls/text-field/text-field.component.tsx, @components/form-controls/chips-choice/chips-choice.component.tsx, @components/form-controls/dictionary/dictionary.component.tsx, @components/form-controls/tag-select/tag-select.component.tsx, @api/actions/method.service.tsx]
 */

import { DatePicker } from '@components/form-controls/date-picker';
import { TextField } from '@components/form-controls/text-field';
import { ChipsChoice } from '@src/components/form-controls/chips-choice';
import { Dictionary } from '@components/form-controls/dictionary';
import { Text } from '@consta/uikit/Text';
import { MethodService } from '@api/actions/method.service';
import { useDebounce } from '@consta/uikit/useDebounce';
import { useEffect } from 'react';
import moment from 'moment';
import { TErrorMessage, FullForm, TBaseObjectDto } from '@src/types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { extractAxiosErrors } from '@utils/error-helper';
import { TagSelect } from '@components/form-controls/tag-select';

export const Form = ({ control, getValues, setValue, setError, formErrors, watch, clearErrors }: FullForm) => {
    const onChange = async () => {
        clearErrors();
        const item = getValues();
        const response: AxiosResponse | AxiosError = await MethodService.getPlannedExecutionDate({
            executor: item.executor,
            laboriousnessPlan: item.laboriousnessPlan,
            predecessors: item.predecessors?.map((item: TBaseObjectDto) => item.$item.id),
            dateStartPlan: item.dateStartPlan,
            dateStartFact: item.dateStartFact,
            dateEndFact: item.dateEndFact,
            dateEndPlan: item.dateEndPlan
        });

        if (axios.isAxiosError(response)) {
            const errors: TErrorMessage[] = extractAxiosErrors(response);
            errors.map(({ field, message }: TErrorMessage) => {
                field && setError(field, { type: 'required', message });
            });
            return;
        }

        const responseData = response.data;

        if (!responseData) {
            return;
        }

        responseData.dateStartPlan && setValue('dateStartPlan', responseData.dateStartPlan);
        responseData.dateStartFact && setValue('dateStartFact', responseData.dateStartFact);
        responseData.dateEndPlan && setValue('dateEndPlan', responseData.dateEndPlan);
        responseData.dateEndFact && setValue('dateEndFact', responseData.dateEndFact);
        responseData.laboriousnessFact && setValue('laboriousnessFact', responseData.laboriousnessFact);
    };
    const debounceChangeValues = useDebounce(onChange, 500);

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (type === 'change' && name && plannedExecutionMethodFields.includes(name)) {
                debounceChangeValues();
            }

            if (type === 'change' && name === 'state' && value.state?.id) {
                if (value.state.id === '4') {
                    setValue('dateStartFact', moment().valueOf());
                    onChange();
                }

                if (value.state.id === '5') {
                    if (!getValues('dateStartFact')) {
                        setValue('dateStartFact', moment().valueOf());
                    }

                    setValue('dateEndFact', moment().valueOf());
                    setValue('progress', 100);
                    onChange();
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [watch]);

    return (
        <div className="flex flex-col gap-[1.0585vmin]">
            <Dictionary label="Сервис" name="service" control={control} />

            <Dictionary label="Проект" name="project" control={control} />

            <Dictionary label="Спринт" name="sprint" control={control} />

            {/* <Combobox label="Тэги" name="tags" multiple={true} control={control} /> */}

            <TagSelect label="Тэги" placeholder="Выберите тэг" name="tags" control={control} />

            {/* <Dictionary label="ФТ" name="functionalRequirement" control={control} /> */}

            <ChipsChoice label="Приоритет" name="priority" control={control} view="priority" />

            <Dictionary
                label="Ответственный"
                name="executor"
                control={control}
                status={formErrors.executor ? 'alert' : undefined}
            />

            <div className="flex flex-col gap-[0.4234vmin]">
                <Text size="s" className="whitespace-nowrap dictionary__label font-medium leading-[2.5405vmin]">
                    Трудоёмкость
                </Text>

                <div className="flex gap-default">
                    <TextField
                        label="План, дн"
                        type="number"
                        incrementButtons={false}
                        name="laboriousnessPlan"
                        control={control}
                        status={formErrors.laboriousnessPlan ? 'alert' : undefined}
                        className="min-w-[8.2566vmin] w-[8.2566vmin]"
                    />

                    <TextField
                        label="Факт, дн"
                        type="number"
                        incrementButtons={false}
                        readOnly={true}
                        name="laboriousnessFact"
                        control={control}
                        status={formErrors.laboriousnessFact ? 'alert' : undefined}
                        className="min-w-[8.2566vmin] w-[8.2566vmin]"
                    />

                    <TextField
                        label="% выполнения"
                        type="number"
                        min={0}
                        max={100}
                        incrementButtons={false}
                        name="progress"
                        control={control}
                    />
                </div>
            </div>

            <div className="flex flex-col">
                <Text size="s" className="whitespace-nowrap dictionary__label font-medium leading-[2.5405vmin]">
                    Начало
                </Text>

                <div className="flex gap-default">
                    <DatePicker label="План" name="dateStartPlan" control={control} />

                    <DatePicker label="Факт" name="dateStartFact" control={control} />
                </div>
            </div>

            <div className="flex flex-col">
                <Text size="s" className="whitespace-nowrap dictionary__label font-medium leading-[2.5405vmin]">
                    Окончание
                </Text>

                <div className="flex gap-default">
                    <DatePicker label="План" name="dateEndPlan" control={control} />

                    <DatePicker label="Факт" name="dateEndFact" control={control} />
                </div>
            </div>
        </div>
    );
};

const plannedExecutionMethodFields = [
    'executor',
    'laboriousnessPlan',
    'laboriousnessFact',
    'dateStartPlan',
    'dateStartFact',
    'dateEndPlan',
    'dateEndFact',
    'predecessors'
];
